// File: app/src/hooks/useExerciseHistory.js
//
// Aggregates per-phase actuals from saved sessions into a per-exercise-key
// history map: { exerciseKey: { perRepSec, samples } }.
//
// The map is consumed by the workout compiler (via WorkoutContext) so the
// rep estimator can blend the deterministic formula with the user's actual
// pace once they have ≥3 logged samples for a given exercise.
//
// Pure derivation from `sessions` provided by UserProfileContext — no I/O.

import { useMemo } from "react";
import { useUserProfile } from "../contexts/UserProfileContext";
import { calHistoryKey, normaliseExerciseKey } from "../utils/repEstimator";

// Maximum number of completed sessions to scan. Recent sessions are weighted
// equally; older sessions are ignored to keep the map responsive when a user
// changes pace or programme.
const MAX_SESSIONS = 50;

// Reject entries that look corrupt or unrepresentative.
const isUsableEntry = (entry) => {
  if (!entry || typeof entry !== "object") return false;
  if (!Number.isFinite(entry.actualDurationSec)) return false;
  if (entry.actualDurationSec <= 0) return false;
  // Stopped-mid-phase entries underestimate; skip them.
  if (entry.skipReason === "sessionEnded") return false;
  return true;
};

// Build the history map from a list of sessions. Exported for testing.
export const buildExerciseHistoryMap = (sessions) => {
  if (!Array.isArray(sessions) || sessions.length === 0) return {};

  // Take the most recent N sessions. Assume `sessions` is roughly
  // chronological; we slice from the tail to favour recent data.
  const recent = sessions.slice(-MAX_SESSIONS);

  // Accumulate per-exercise totals. A counted entry carries the target it was
  // prescribed, so duration ÷ count is a true per-unit pace; untimed holds and
  // timed phases carry no count and are skipped.
  const buckets = {};

  for (const session of recent) {
    const log = session?.plan_data?.phase_log;
    if (!Array.isArray(log)) continue;

    for (const entry of log) {
      if (!isUsableEntry(entry)) continue;

      // Re-derive the key from the label whenever there is one, so history
      // follows the exercise library as it improves (#84) instead of staying
      // stranded under whatever key was current when it was logged. The stored
      // key is only a fallback for entries with no label.
      const key = entry.nodeLabel
        ? normaliseExerciseKey(entry.nodeLabel)
        : entry.exerciseKey;
      if (!key || key === "default") continue;

      // Only COUNTED entries (reps or calories) contribute — a phase the user
      // simply held for a duration says nothing about their per-unit pace.
      // Calorie entries bucket under their own key namespace so seconds-per-cal
      // can never be read back as seconds-per-rep for the same machine.
      // Legacy entries predate countTarget/countUnit and carry repCount only.
      const isCal = entry.countUnit === "cal";
      const count = Number(entry.countTarget ?? entry.repCount);
      if (!Number.isFinite(count) || count <= 0) continue;

      const perUnit = entry.actualDurationSec / count;
      if (!Number.isFinite(perUnit) || perUnit <= 0) continue;

      const bucketKey = isCal ? calHistoryKey(entry.nodeLabel || key) : key;
      if (!buckets[bucketKey]) {
        buckets[bucketKey] = { totalPerRepSec: 0, samples: 0 };
      }
      buckets[bucketKey].totalPerRepSec += perUnit;
      buckets[bucketKey].samples += 1;
    }
  }

  // Reduce to the public shape consumed by repEstimator.
  const result = {};
  for (const [key, b] of Object.entries(buckets)) {
    result[key] = {
      perRepSec: b.totalPerRepSec / b.samples,
      samples: b.samples,
    };
  }
  return result;
};

/**
 * React hook that returns a memoised per-exercise history map.
 *
 * @returns {Object} { exerciseKey: { perRepSec: number, samples: number } }
 */
const useExerciseHistory = () => {
  const { sessions } = useUserProfile();
  return useMemo(() => buildExerciseHistoryMap(sessions), [sessions]);
};

export default useExerciseHistory;
